import React, { useEffect, useState, useCallback } from "react";
import { listPending, oldestPendingAt, syncPending, getMeta, fireStaleNotifIfDue } from "@/lib/offline";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { RefreshCw, WifiOff } from "lucide-react";

export function SyncBanner({ onSyncComplete }) {
  const { t, lang } = useLang();
  const [pending, setPending] = useState(0);
  const [oldest, setOldest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const refresh = useCallback(async () => {
    const items = await listPending();
    setPending(items.length);
    setOldest(await oldestPendingAt());
  }, []);

  useEffect(() => {
    refresh();
    fireStaleNotifIfDue();
    const onOn = () => sync();
    window.addEventListener("online", onOn);
    // Retry every 2 minutes
    const iv = setInterval(() => { if (navigator.onLine) sync(); }, 120_000);
    return () => { window.removeEventListener("online", onOn); clearInterval(iv); };
    // eslint-disable-next-line
  }, []);

  const sync = async () => {
    if (busy) return;
    setBusy(true);
    setProgress(null);
    const res = await syncPending(api, (p) => setProgress(p));
    setBusy(false);
    refresh();
    onSyncComplete?.(res);
  };

  if (pending === 0) return null;

  const ageH = oldest ? (Date.now() - new Date(oldest).getTime()) / 3.6e6 : 0;
  const isStale = ageH > 24;
  const cls = isStale ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800";
  const oldestLabel = oldest
    ? new Date(oldest).toLocaleString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div data-testid="sync-banner" className={`border rounded-md p-3 flex items-center gap-3 flex-wrap ${cls}`}>
      <WifiOff size={16} />
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-semibold">
          {lang === "hi"
            ? `${pending} निरीक्षण सिंक बाकी${oldest ? ` — ${oldestLabel} से` : ""}`
            : `${pending} inspection${pending > 1 ? "s" : ""} pending sync${oldest ? ` since ${oldestLabel}` : ""}`}
        </div>
        {isStale && (
          <div className="text-xs mt-0.5" data-testid="sync-banner-stale">
            {lang === "hi" ? "24 घंटे से ज़्यादा हो गए — कृपया अभी कनेक्ट करें।" : "Over 24 hours old — please connect and sync now."}
          </div>
        )}
        {progress && (
          <div className="text-xs mt-0.5 text-[#4A627A]">{progress.status}…</div>
        )}
      </div>
      <button
        data-testid="sync-now-btn"
        disabled={busy}
        onClick={sync}
        className="tap-target px-3 rounded-md font-semibold text-sm bg-white border border-black/10 flex items-center gap-1"
      >
        <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
        {lang === "hi" ? "अभी सिंक" : "Sync now"}
      </button>
    </div>
  );
}
